import { NotebookDtoType } from '@convergence/client';
import { NotebookContent } from '@convergence/types';
import { CheckIcon, XIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { HomeStore } from '../stores/HomeStore';
import { NotebookStore } from '../stores/NotebookStore';
import { TinkerBoxConnectionStore } from '../stores/TinkerBoxConnectionStore';
import { Notebook } from './Notebook';
import { useSaveNotebookMutation } from '../api/mutations/saveNotebook';
import { toast } from 'react-toastify';

interface NotebookContainerProps {
  notebook: NotebookDtoType;
  tinkerBoxConnection: TinkerBoxConnectionStore;
  homeStore: HomeStore;
  editNotebookTitle: (id: string, newTitle: string) => Promise<NotebookDtoType>;
  className?: string;
}

export const NotebookContainer = observer(
  ({
    notebook,
    tinkerBoxConnection,
    homeStore,
    editNotebookTitle,
    className
  }: NotebookContainerProps) => {
    const { mutateAsync: saveNotebook } = useSaveNotebookMutation();
    const content: NotebookContent = JSON.parse(notebook.content);
    const [store] = useState(
      new NotebookStore(homeStore.socket, tinkerBoxConnection)
    );

    useEffect(() => {
      store.setContent(content);
    }, [notebook, store]);

    const [editingTitle, setEditingTitle] = useState(false);
    const [newTitle, setNewTitle] = useState(notebook.title);
    const [isHovering, setIsHovering] = useState(false);

    useEffect(() => {
      store.init();
    }, [store]);

    useEffect(() => {
      return () => {
        if (!notebook.readonly) {
          console.log('component unmounted, saving notebook');
          saveNotebook({
            data: {
              content: JSON.stringify(store.notebookContent)
            },
            title: notebook.title
          });
        }
      };
    }, []);

    return (
      <DndProvider backend={HTML5Backend} context={window}>
        <div className={`${className || ''} h-full flex flex-col`}>
          <div className="py-2 flex justify-between items-center shrink-0">
            {editingTitle ? (
              <div>
                <input
                  type="text"
                  value={newTitle}
                  autoFocus
                  onChange={(e) => {
                    setNewTitle(e.target.value);
                  }}
                />
                <button
                  className="p-1 text-neutral-500 hover:text-black"
                  onClick={async () => {
                    try {
                      await editNotebookTitle(notebook.id, newTitle);
                      homeStore.setSelectedNotebookTitle(newTitle);
                      setEditingTitle(false);
                    } catch (err) {}
                  }}
                >
                  <CheckIcon size={16} />
                </button>
                <button
                  className="p-1 text-neutral-500 hover:text-black"
                  onClick={() => {
                    setEditingTitle(false);
                    setNewTitle(notebook.title);
                  }}
                >
                  <XIcon size={16} />
                </button>
              </div>
            ) : (
              <div
                className="flex items-center"
                onClick={() => {
                  setEditingTitle(true);
                }}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                <h2 className="text-lg">{notebook.title}</h2>
                {isHovering && !notebook.readonly && (
                  <button className="px-2 text-neutral-500 hover:text-black">
                    <PencilSimpleIcon size={18} />
                  </button>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <button
                className="flex gap-1 items-center px-3 py-1 border border-black"
                onClick={() => homeStore.setSelectedNotebookTitle(null)}
              >
                back to list
              </button>
              {!notebook.readonly && (
                <button
                  className="px-3 py-1 bg-black text-white text-white"
                  onClick={async () => {
                    await toast.promise(
                      saveNotebook({
                        data: {
                          content: JSON.stringify(store.notebookContent)
                        },
                        title: notebook.title
                      }),
                      {
                        success: 'Notebook Saved!',
                        error: "Couldn't save"
                      }
                    );
                  }}
                >
                  save
                </button>
              )}
            </div>
          </div>
          <Notebook store={store} readonly={notebook.readonly} />
        </div>
      </DndProvider>
    );
  }
);
